import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Card,
  CardContent,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Google as GoogleIcon,
  AdminPanelSettings as AdminIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const handleGoogleLogin = () => {
    // Use nullish coalescing — `||` would treat the production empty-string env
    // var as falsy and fall back to localhost, sending real users to a non-existent
    // local server. Empty string yields a relative /api path so this works on
    // whichever domain the page is loaded from.
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL ?? '';
    window.location.href = `${apiBaseUrl}/api/auth/google`;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
            minHeight: '80vh'
          }}
        >
          {/* Left Side - Branding */}
          <Box
            sx={{
              flex: 1,
              textAlign: 'center',
              color: 'white',
              mb: isMobile ? 3 : 0
            }}
          >
            <AdminIcon sx={{ fontSize: 80, mb: 2, opacity: 0.9 }} />
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                fontSize: isMobile ? '2.5rem' : '3.5rem',
                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}
            >
              St. Bridget
            </Typography>
            <Typography
              variant="h4"
              component="h2"
              gutterBottom
              sx={{
                fontWeight: 400,
                opacity: 0.9,
                fontSize: isMobile ? '1.5rem' : '2rem',
                mb: 3
              }}
            >
              Staff Portal
            </Typography>
            <Typography
              variant="h6"
              sx={{
                opacity: 0.8,
                maxWidth: 400,
                margin: '0 auto',
                lineHeight: 1.6
              }}
            >
              Manage Religious Education and Parish registrations from your centralized dashboard
            </Typography>
          </Box>

          {/* Right Side - Login Form */}
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            <Card
              elevation={8}
              sx={{
                maxWidth: 450,
                width: '100%',
                borderRadius: 3,
                overflow: 'hidden'
              }}
            >
              <Paper
                sx={{
                  background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                  color: 'white',
                  p: 3,
                  textAlign: 'center'
                }}
              >
                <SecurityIcon sx={{ fontSize: 48, mb: 1 }} />
                <Typography variant="h5" component="h3" sx={{ fontWeight: 600 }}>
                  Secure Staff Access
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, mt: 1 }}>
                  Sign in with your authorized Google account
                </Typography>
              </Paper>

              <CardContent sx={{ p: 4 }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Typography variant="h6" color="text.primary" gutterBottom>
                    Welcome Back
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Access your staff dashboard to manage registrations, settings, and more
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={handleGoogleLogin}
                  startIcon={<GoogleIcon />}
                  sx={{
                    py: 1.5,
                    fontSize: '1.1rem',
                    textTransform: 'none',
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #db4437 0%, #e57373 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #c23321 0%, #d32f2f 100%)',
                      transform: 'translateY(-1px)',
                      boxShadow: theme.shadows[6]
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Continue with Google
                </Button>

                <Divider sx={{ my: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Authorized Personnel Only
                  </Typography>
                </Divider>

                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Need access to the staff portal?
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Contact your system administrator
                  </Typography>
                </Box>
              </CardContent>

              {/* Footer Info */}
              <Paper
                sx={{
                  backgroundColor: '#f5f5f5',
                  p: 2,
                  textAlign: 'center',
                  borderTop: 1,
                  borderColor: 'divider'
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  St. Bridget University Parish Registration System
                </Typography>
              </Paper>
            </Card>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Login; 